import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { BoardType } from '@main-application/boards/interfaces/board.enums';
import {
  DateFieldsLookupMode,
  QueryType,
  ScheduleByStatusFilter,
} from '@main-application/boards/services/turn-step-schedule.service';
import { InspectionByStatusFilter } from '@main-application/inspections/models/rest-inspections-model.interface';
import { SummaryStatsV2FilterOption } from '@main-application/inspections/services/inspection.service';
import { SummaryType } from '@main-application/shared/services/summary-report-settings.service';
import { PriorityType } from '@shared/enums/priority-type';
import { TicketCompletedStatusType, TicketStatusType } from '@shared/enums/ticket-status-type';
import {
  SummaryStatsGroup,
  SummaryStatsV2Filter,
  SummaryStatsV2Params,
} from '@ui-components/components/summary-expandable-table/summary-expandable-table.model';

import { RestapiService } from './restapi.service';
import { ServiceSummaryGroupType, TicketByStatusFilter } from './tickets.service';

export enum SortOrder {
  Ascending = 'asc',
  Descending = 'desc',
}

export enum MenuOptions {
  Save,
  Edit,
  SaveAsNew,
}

export enum SummaryReportView {
  NA = 0,
  IncompleteByStatus = 1,
  IncompleteByPeriod = 2,
  CompleteByPeriod = 3,
  CompleteBySatisfaction = 4,
}

export interface FieldInfoModel {
  fieldType?: number;
  customFieldId?: number;
  label?: string;
  customFieldModifier?: number;
  values?: string;
  labels?: string;
  isDescending?: boolean;
  sortOrder?: SortOrder;
}

export enum PeriodDateSearchFields {
  AnyTimeframe,
  DueDateOnly,
  CreatedAtDateOnly,
}

export interface SummaryWorkloadParams {
  doNotStoreParams: boolean;
  periodDateSearchFields?: PeriodDateSearchFields;
  portfolioId?: number;
  collectionIds?: string;
  propertyIds?: string;
  assigneeIds?: number;
  queryType?: QueryType;

  startDate?: string;
  endDate?: string;

  reportingCategoryIds?: string; // Service Summary
  reportingCategoryNames?: string; // Service Summary
  categoryIds?: string; // Inspections Summary
  categoryNames?: string; // Inspections Summary
  boardIds?: string; // BB Summary
  boardNames?: string; // BB Summary

  serviceByStatusFilter?: TicketByStatusFilter; // Service Summary
  isCompleted?: TicketCompletedStatusType;
  inspectionByStatusFilter?: InspectionByStatusFilter; // Inspections Summary
  scheduleByStatusFilter?: ScheduleByStatusFilter; // BB Summary
}

export class SummaryTicketFilters {
  timeframe: number;
  isCompleted: number;
  statuses: TicketStatusType[];

  // v2 filters
  serviceByStatusFilter: TicketByStatusFilter;
  portfolioId: number;
  portfolioName: string;
  propertyIds: string;
  propertyNames: string;
  collectionIds: string;
  collectionNames: string;
  unitType: string;
  unitTypes: string;
  unitTypeNames: string;
  templateIds: string;
  templateNames: string;
  boardType: BoardType;
  boardTypeNames: string;
  boardTypes: string;
  priority: PriorityType;
  priorities: string;
  prioritiesNames: string;
  assigneeIds: string;
  assigneeNames: string;
  clientServiceRateAsSatisfiedIds: string;
  clientServiceRateAsSatisfiedNames: string;
  activityIds: string;
  activityNames: string;
  turnCurrentStepAssigneeIds: string;
  turnCurrentStepAssigneeNames: string;
  scheduleIds: string;
  scheduleNames: string;
  reportingCategoryIds: string;
  reportingCategoryNames: string;
  reportingTopicIds: string;
  reportingTopicNames: string;
  ticketCategoryIds: string;
  ticketCategoryNames: string;
  topicIds: string;
  topicNames: string;
  boardIds: string;
  boardNames: string;
  startDate: string;
  endDate: string;
  periodDateSearchFields: number;
  includeCompleted: boolean;
  doNotReturnContent: boolean;
  queryType?: number;
  dateFieldsLookupMode: DateFieldsLookupMode;
  hasIssuesIds: string;
  hasIssuesNames: string;
}

@Injectable({
  providedIn: 'root',
})
export class SummaryService extends PuSubscribable {
  restApiService = inject(RestapiService);

  private _showByData$ = new BehaviorSubject<FieldInfoModel[]>([]);
  private _sortByGroup$ = new BehaviorSubject<{ columnName: string | null; isDescending: boolean | null }[]>([]);

  getSummaryTypeBase(summaryType: SummaryType, main = true): string {
    switch (summaryType) {
      case SummaryType.Turns:
        return 'turnovers';
      case SummaryType.BB:
        return main ? 'turnovers/bb' : 'turnstepschedule';
      case SummaryType.Inspection:
        return 'inspections';
      case SummaryType.Service:
        return 'tickets';
      default:
        throw new Error(`Unsupported SummaryType: ${summaryType}`);
    }
  }

  getSummaryStatsV2(
    summaryType: SummaryType,
    query: SummaryStatsV2Params,
    filters: SummaryStatsV2Filter[]
  ): Observable<SummaryStatsGroup[]> {
    const queryParts: string[] = [];

    if (
      (query.groupBy === null || query.groupBy === undefined) &&
      (query.subGroupBy === null || query.subGroupBy === undefined) &&
      (query.groupByCustomFieldId === null || query.groupByCustomFieldId === undefined) &&
      (query.subgroupByCustomFieldId === null || query.subgroupByCustomFieldId === undefined)
    ) {
      return of([]);
    }

    if (query.portfolioId != null) queryParts.push(`portfolioId=${query.portfolioId}`);
    if (query.propertyIds) queryParts.push(`propertyIds=${query.propertyIds}`);
    if (query.collectionIds) queryParts.push(`collectionIds=${query.collectionIds}`);
    if (query.startDate) queryParts.push(`startDate=${query.startDate}`);
    if (query.endDate) queryParts.push(`endDate=${query.endDate}`);
    if (query.isResident != null) queryParts.push(`isResident=${query.isResident}`);
    if (query.includeUnscheduled != null) queryParts.push(`includeUnscheduled=${query.includeUnscheduled}`);
    if (query.queryType != null) queryParts.push(`queryType=${query.queryType}`);
    if (query.dateFieldsLookupMode != null) queryParts.push(`dateFieldsLookupMode=${query.dateFieldsLookupMode}`);
    if (query.assigneeId != null) queryParts.push(`assigneeId=${query.assigneeId}`);
    if (query.groupBy != null) queryParts.push(`groupBy=${query.groupBy}`);
    if (query.subGroupBy != null) queryParts.push(`subGroupBy=${query.subGroupBy}`);
    if (query.groupByCustomFieldId != null) queryParts.push(`groupByCustomFieldId=${query.groupByCustomFieldId}`);
    if (query.groupByCustomFieldModifier != null)
      queryParts.push(`groupByCustomFieldModifier=${query.groupByCustomFieldModifier}`);
    if (query.subgroupByCustomFieldId != null)
      queryParts.push(`subgroupByCustomFieldId=${query.subgroupByCustomFieldId}`);
    if (query.subgroupByCustomFieldModifier != null)
      queryParts.push(`subgroupByCustomFieldModifier=${query.subgroupByCustomFieldModifier}`);
    if (query.excludeBoardTickets) queryParts.push(`excludeBoardTickets=${query.excludeBoardTickets}`);
    if (query.periodDateSearchFields) queryParts.push(`periodDateSearchFields=${query.periodDateSearchFields}`);

    const base = this.getSummaryTypeBase(summaryType);
    const url = `${base}/summaryStats/v2?${queryParts.join('&')}`;

    return this.restApiService.post<SummaryStatsGroup[], SummaryStatsV2Filter[]>(url, filters);
  }

  getSummaryStatsV2Filters(
    summaryType: SummaryType,
    filterType?: ServiceSummaryGroupType,
    search?: string
  ): Observable<SummaryStatsV2FilterOption[]> {
    const params: string[] = [];

    if (filterType !== undefined && filterType !== null) {
      params.push(`filterType=${filterType}`);
    }

    if (search && search.trim() !== '') {
      params.push(`search=${encodeURIComponent(search)}`);
    }

    const base = this.getSummaryTypeBase(summaryType, false);
    const url = `${base}/summaryStats/v2/filters${params.length ? '?' + params.join('&') : ''}`;
    return this.restApiService.getData<SummaryStatsV2FilterOption[]>(url);
  }

  setShowByData(data: FieldInfoModel[]): void {
    this._showByData$.next(data);
  }

  get showByData$(): Observable<FieldInfoModel[]> {
    return this._showByData$.asObservable();
  }

  get showByDataSnapshot(): FieldInfoModel[] {
    return this._showByData$.value;
  }

  initSortByGroup(sortBy: { columnName?: string; isDescending?: boolean }[], count = 3): void {
    const result = Array.from({ length: count }, (_, index) => {
      const entry = sortBy?.[index];
      return {
        columnName: entry?.columnName ?? null,
        isDescending: !entry?.columnName ? null : entry?.isDescending ?? null,
      };
    });

    this._sortByGroup$.next(result);
  }

  setSortByGroup(data: { columnName: string; isDescending: boolean; reportView: number } | null, count = 3): void {
    const current = this._sortByGroup$.value;

    const result = Array.from({ length: count }, (_, index) => {
      if (data && data.reportView - 1 === index) {
        return {
          columnName: data.columnName,
          isDescending: data.isDescending,
        };
      }

      return current?.[index] ?? { columnName: null, isDescending: null };
    });

    this._sortByGroup$.next(result);
  }

  get sortByGroup$(): Observable<{ columnName: string | null; isDescending: boolean | null }[]> {
    return this._sortByGroup$.asObservable();
  }

  get sortByGroupSnapshot(): { columnName: string | null; isDescending: boolean | null }[] {
    return this._sortByGroup$.value;
  }
}
